import React, { useState, useRef, useCallback, useEffect } from "react";
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalTitle,
} from "react-bootstrap";
import ModalHeader from "react-bootstrap/esm/ModalHeader";
import { useIdleTimer } from "react-idle-timer";
import { useSession } from "../contexts/SessionContext";

const WARNING_DURATION = window.APP_CONFIG.REACT_APP_SESSION_WARNING_DURATION;
const ACTIVITY_CHECK_INTERVAL =
  window.APP_CONFIG.REACT_APP_ACTIVITY_CHECK_INTERVAL;

const TimeoutSession = () => {
  const { sessionTimeout, refreshSession, isLoggedIn, logout } = useSession();

  const [modalOpen, setModalOpen] = useState(false);
  const [countdown, setCountdown] = useState(WARNING_DURATION / 1000);

  const hasActivityRef = useRef(false);
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  // the absolute timestamp at which the countdown should reach zero and logout
  const logoutAtRef = useRef<number | null>(null);

  /**
   * Clears countdown interval and resets reference to null
   */
  const clearCountdownInterval = useCallback(() => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  }, []);

  /**
   * Starts the 20-second countdown timer displayed in the warning modal.
   * Automatically logs out the user when countdown reaches zero.
   *
   * background or inactive browser tabs throttle setInterval, the tick-counting approach
   * would silently loses seconds in this case.
   */
  const startCountdown = useCallback(() => {
    clearCountdownInterval();
    logoutAtRef.current = Date.now() + WARNING_DURATION;
    setCountdown(WARNING_DURATION / 1000);

    countdownIntervalRef.current = setInterval(() => {
      const remainingMs = (logoutAtRef.current ?? 0) - Date.now();
      setCountdown(Math.max(Math.ceil(remainingMs / 1000), 0));
    }, 1000);
  }, [clearCountdownInterval]);

  /**
   * separate effect that watches the countdown value and react to it when it reaches 0
   */
  useEffect(() => {
    if (countdown === 0 && modalOpen) {
      clearCountdownInterval();
      setModalOpen(false);
      logout();
    }
  }, [countdown, modalOpen, logout, clearCountdownInterval]);

  /**
   * Handles user interaction events
   */
  const handleOnAction = useCallback(
    (event: React.KeyboardEvent | React.MouseEvent) => {
      if (event.type === "keydown" && "key" in event && event.key === "F5") {
        return;
      }
      hasActivityRef.current = true;
    },
    [],
  );

  /**
   * Timeout handler - triggered when the primary idle timer expires
   */
  const handleIdleTimeout = useCallback(() => {
    if (!isLoggedIn) return;
    setModalOpen(true);
    startCountdown();
  }, [startCountdown, isLoggedIn]);

  /**
   * Idle timer configuration
   */
  const {
    reset: resetMainTimer,
    start: startMainTimer,
    pause: pauseMainTimer,
  } = useIdleTimer({
    timeout: sessionTimeout - WARNING_DURATION,
    onIdle: handleIdleTimeout,
    onAction: handleOnAction,
    events: ["mousedown", "keydown", "touchstart", "scroll"],
    startManually: true,
  });

  /**
   * Handles user choice to stay logged in
   */
  const handleStayLoggedIn = useCallback(() => {
    setModalOpen(false);
    clearCountdownInterval();
    resetMainTimer();
    refreshSession();
  }, [resetMainTimer, refreshSession, clearCountdownInterval]);

  /**
   * Regularly check the activity flag
   */
  const checkActivityAndRefresh = useCallback(async () => {
    if (hasActivityRef.current && isLoggedIn) {
      await refreshSession();
      hasActivityRef.current = false;
    }
  }, [refreshSession, isLoggedIn]);

  /**
   * Sets up periodic session refresh and starts the idle timer
   */
  useEffect(() => {
    if (!isLoggedIn) return;

    startMainTimer();

    refreshIntervalRef.current = setInterval(
      checkActivityAndRefresh,
      ACTIVITY_CHECK_INTERVAL,
    );

    return () => {
      pauseMainTimer();
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    };
    // add sessionTimeout into dependency, to restart the timer explicitly when it changes
  }, [
    isLoggedIn,
    sessionTimeout,
    startMainTimer,
    pauseMainTimer,
    checkActivityAndRefresh,
  ]);

  /**
   * recompute the countdown immediately when tab regains visibility
   * while the tab was backgrounded, the setInterval was throttled by browser
   * this function forces time re-syn at the moment user is back
   */
  const resyncCountdown = useCallback(() => {
    if (logoutAtRef.current == null) return;
    const remainingMs = logoutAtRef.current - Date.now();
    setCountdown(Math.max(Math.ceil(remainingMs / 1000), 0));
  }, []);

  /**
   * Handles browser tab visibility changes
   */
  useEffect(() => {
    if (!isLoggedIn) return;

    const handleVisibilityChange = () => {
      if (document.hidden || !modalOpen) return;
      resyncCountdown();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isLoggedIn, modalOpen, resyncCountdown]);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      clearCountdownInterval();
    };
  }, [clearCountdownInterval]);

  if (!isLoggedIn) return null;

  return (
    <>
      {isLoggedIn && (
        <Modal
          role="dialog"
          show={modalOpen}
          onHide={handleStayLoggedIn}
          backdrop="static"
          keyboard={false}
        >
          <ModalHeader closeButton>
            <ModalTitle>Achtung!</ModalTitle>
          </ModalHeader>
          <ModalBody>
            <p aria-live="polite">
              Sie werden ausgeloggt, wenn Sie nichts tun. Es verbleiben{" "}
              <b>{countdown}</b> Sekunden.
            </p>
          </ModalBody>
          <ModalFooter>
            <Button variant="primary" onClick={handleStayLoggedIn}>
              Eingeloggt bleiben
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </>
  );
};

export default TimeoutSession;
